<template>
  <section class="section-summary">
    <div class="card shadow-sm" v-if="preBookingData">
      <div class="card-body">
        <div class="row">
          <div class="col-12 p-0">
            <h3 class="reply-name">{{ $t('message.booking.summary.title') }}</h3>
            <b><span v-html="$options.filters.serviceParentName(preBookingData.service_id, globalState.services)"></span></b>
          </div>
          <div class="col-12 p-0 mt-1">
            <span v-if="preBookingData.service_id === 1">
              {{ getWorkHours() }}
            </span>
            <span v-else-if="preBookingData.service_items">
              <div v-for="serviceItem in preBookingData.service_items">
                - {{ serviceItem | multiLangName }}: {{ serviceItem.count }} {{ serviceItem.item_unit | multiLangName }}
              </div>
            </span>
            <span v-else>
              {{ globalState.services[preBookingData.service_id] | multiLangName }}
            </span>
          </div>
          <div class="col-12 p-0 mt-2" v-if="preBookingData.subscription_type_id">
            <span class="text-orange">
              <i class="fal fa-calendar-alt"></i> {{ $t('message.subscription.title') }}
            </span>
            <span> - {{ subscriptionType | multiLangName }} ({{ subscriptionType.num_per_month }} {{ $t('message.times') }})</span>
            <div>
              {{ preBookingData.cleaning_date | subscriptionStartDate }} • {{ preBookingData.cleaning_time }}
            </div>
          </div>
          <div class="col-12 p-0 mt-2" v-else-if="preBookingData.package_type">
            <span class="text-orange">
              <i class="fal fa-layer-group"></i> {{ $t('message.my_reservations.package') }}
            </span>
            <span> - {{ preBookingData.package_type | multiLangName }}</span>
            <span v-if="preBookingData.package_type.category === 'flexible'">
              {{ $t('message.booking.new_booking.package_count', { count: preBookingData.package_type_discount.booking_qty }) }}
            </span>
            <span v-else-if="preBookingData.package_type.category === 'unlimited'">
              {{ $t('message.booking.new_booking.package_count', { count: preBookingData.multi_dates_times.length }) }}
            </span>
            <section class="mt-2 pb-0 mb-0">
              <ul class="ml-4 pb-0 mb-0">
                <li class="text-order-title" v-for="selectedDateTime in preBookingData.multi_dates_times">
                  {{ selectedDateTime | shortDate }} • {{ selectedDateTime | onlyTime }}
                </li>
              </ul>
            </section>
          </div>
          <div class="col-12 p-0 mt-2" v-else>
            <span v-if="preBookingData.cleaning_date">
              {{ preBookingData.cleaning_date | summaryCleaningDate }} • {{ preBookingData.cleaning_time }}
            </span>
            <span v-else>
              {{ $t('message.booking.new_booking.booking_type_flexible') }}
            </span>
          </div>
          <div class="col-12 p-0 mt-1">
            {{ globalState.cities[preBookingData.province_id] | multiLangName }}
          </div>
        </div>

        <section v-if="!_.isEmpty(selectedProfessional)">
          <hr>
          <div>
            <label>{{ $t('message.booking.summary.your_cleaner') }}</label>
          </div>
          <div class="d-flex justify-content-start align-items-center">
            <div>
              <template v-if="selectedProfessional">
                <professional-picture :professional="selectedProfessional"
                                      :imgClass="'img-fluid'"
                                      :imgSize="44"
                                      :badge-size="20"/>
              </template>
              <template v-else>
                <img src="/static/img/unselected-cleaner.png" width="56" alt="unselected cleaner">
              </template>
            </div>
            <div v-if="selectedProfessional" class="pl-3">
              {{ selectedProfessional.name | firstName }}
              <p v-if="$route.path==='/booking/choose-cleaner'">
                <a href="#" @click.prevent="unselectProfessional" class="text-remove-cleaner">
                  <i class="fas fa-times"></i>&nbsp;{{ $t('message.booking.summary.remove') }}
                </a>
              </p>
            </div>
          </div>
          <div class="d-flex border border-warning justify-content-start align-items-top -bg-warning-soft rounded-all p-2 mt-2 text-small"
               v-if="distance && acceptedDistance && distance > acceptedDistance">
            <div>
              <i class="fal fa-exclamation-circle text-danger fa-lg"></i>
            </div>
            <div class="pl-2">
              <div>
                <strong>{{ $t('message.booking.checkout_step.distance_warning_title') }} {{ distance | distance }}</strong>
              </div>
              <div>
                {{ $t('message.booking.checkout_step.distance_warning_subtitle') }}
              </div>
            </div>
          </div>
        </section>

        <!-- Checkout cost -->
        <checkout-cost/>
        <label class="mt-4">{{ $t('message.booking.summary.all_inclusive_title') }}</label>
        <div class="text-remark">
          <span v-if="preBookingData.service_id === 1">
            {{ $t('message.booking.summary.all_inclusive_general_cleaning') }}
          </span>
          <span v-else-if="preBookingData.service_id >= 2 && preBookingData.service_id <= 6">
            {{ $t('message.booking.summary.all_inclusive_ironing') }}
          </span>
          <span v-else>
            {{ $t('message.booking.summary.all_inclusive_transportation') }}
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import PromoCode from '../components/PromoCode'
  import CheckoutCost from '../components/CheckoutCost'
  import ProfessionalPicture from '../../professional/components/ProfessionalPicture'
  import subscriptionHelpers from '../../../helpers/subscriptionHelpers'
  import userPlaceHelpers from '../../../helpers/userPlaceHelpers'

  export default {
    components: {
      CheckoutCost,
      PromoCode,
      ProfessionalPicture
    },
    data () {
      return {
        subscriptionType: null,
        distance: null,
        acceptedDistance: null
      }
    },
    computed: {
      ...mapGetters({
        appliedExtras: 'extras/getAppliedExtras',
        globalState: 'globalVal/getState',
        hasAppliedExtras: 'extras/getHasAppliedExtras',
        preBookingData: 'booking/getPreBookingData',
        selectedProfessional: 'booking/getSelectedProfessional',
        state: 'booking/getState',
        user: 'auth/getUser'
      }),
      ...mapState('booking', ['preCheckoutData'])
    },
    watch: {
      'preCheckoutData.user_place': function (newVal, oldVal) {
        // Check distance when update new user place location
        if (this.selectedProfessional && this.selectedProfessional.latitude && this.selectedProfessional.longitude) {
          if (newVal && newVal.latitude && newVal.longitude) {
            this.distance = userPlaceHelpers.calculateDistance(newVal.latitude, newVal.longitude, this.selectedProfessional.latitude, this.selectedProfessional.longitude)
          }
        }
      }
    },
    methods: {
      getWorkHours () {
        let workHour = this.preBookingData.work_hour
        if (typeof workHour === 'number') {
          workHour = workHour.toFixed(1)
        }
        return this.$i18n.t(this.globalState.allWorkHours[workHour])
      },
      getSubscriptionTypeData () {
        if (this.preBookingData.subscription_type_id) {
          this.subscriptionType = subscriptionHelpers.getSubscriptionTypeData(this.globalState.subscriptionTypes, this.preBookingData.subscription_type_id)
        }
      }
    },
    mounted () {
      this.getSubscriptionTypeData()
      if (this.preCheckoutData) {
        const acceptedDistance = this.globalState.cities[this.preCheckoutData.province_id].accepted_distance
        this.acceptedDistance = (acceptedDistance) ? Number(acceptedDistance) : null
        // Check distance when init
        if (this.selectedProfessional && this.selectedProfessional.latitude && this.selectedProfessional.longitude) {
          const userPlace = this.preCheckoutData.user_place
          if (userPlace && userPlace.latitude && userPlace.longitude) {
            this.distance = userPlaceHelpers.calculateDistance(userPlace.latitude, userPlace.longitude, this.selectedProfessional.latitude, this.selectedProfessional.longitude)
          }
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/booking/components/SummaryBookingDesktop.vue